import React from "react";

/**
 * Fallback UI for {@link ErrorBoundary}. Either a static ReactNode, or a
 * render function that receives the captured error so the caller can format
 * it (e.g. include the error message, an ErrorId, or a retry control).
 */
export type ErrorBoundaryFallback =
  | React.ReactNode
  | ((error: Error) => React.ReactNode);

export interface ErrorBoundaryProps {
  /** Rendered in place of `children` after a render-time error is caught. */
  fallback: ErrorBoundaryFallback;
  children: React.ReactNode;
  /**
   * Prefix prepended to `console.error` when a child throws. Kept stable so
   * log-scanning tools can filter widget errors deterministically.
   *
   * @default "[dashboard-widget-error]"
   */
  logPrefix?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

const DEFAULT_LOG_PREFIX = "[dashboard-widget-error]";

/**
 * Generic React 18 error boundary primitive.
 *
 * Wrap a subtree that might throw during render so a single failing widget
 * doesn't blank the surrounding page. When a descendant throws:
 *
 * 1. The error is stored on state via `getDerivedStateFromError`.
 * 2. `componentDidCatch` logs `${logPrefix} ${error.message}` to
 *    `console.error`, followed by the error and the React error info, so the
 *    original stack survives.
 * 3. `render()` returns `fallback` — invoking it with the captured error if
 *    it's a function, otherwise rendering it as-is.
 *
 * This primitive is intentionally standalone: no hooks-based error boundary
 * exists in React 18, and this component has no dependency on any other
 * dashboard code.
 */
export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const prefix = this.props.logPrefix ?? DEFAULT_LOG_PREFIX;
    // eslint-disable-next-line no-console
    console.error(`${prefix} ${error.message}`, error, errorInfo);
  }

  render(): React.ReactNode {
    if (this.state.hasError && this.state.error) {
      const { fallback } = this.props;
      return typeof fallback === "function"
        ? fallback(this.state.error)
        : fallback;
    }
    return this.props.children;
  }
}
